import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import http from "../../utils/axios";

const initialState = {
  currentTransaction: null,
  transactionSummary: null,
  transactionHistory: [],
  historyMeta: null,
  customer: null,
  loading: false,
  summaryLoading: false,
  historyLoading: false,
  error: null,
  status: "idle", // idle, loading, success, failed
  customerLoading: false,
  customerError: null,
};

// Async thunks with direct API calls (following existing codebase pattern)
export const createTransaction = createAsyncThunk(
  "transaction/create",
  async (data, { rejectWithValue }) => {
    try {
      const response = await http.post("/dashboard/business-owner/transactions/create", data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create transaction"
      );
    }
  }
);

// Keep legacy functions for admin compatibility
export const initiateTransaction = createAsyncThunk(
  "transaction/initiate",
  async (data, { rejectWithValue }) => {
    try {
      const response = await http.post("/dashboard/business-owner/transactions/initiate", data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to initiate transaction"
      );
    }
  }
);

export const fulfillTransaction = createAsyncThunk(
  "transaction/fulfill",
  async ({ uuid, data }, { rejectWithValue }) => {
    try {
      const response = await http.patch(`/dashboard/business-owner/transactions/${uuid}/fulfill`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fulfill transaction"
      );
    }
  }
);

export const getTransaction = createAsyncThunk(
  "transaction/getTransaction",
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await http.get(`/dashboard/business-owner/transactions/${uuid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get transaction"
      );
    }
  }
);

export const cancelTransaction = createAsyncThunk(
  "transaction/cancel",
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await http.patch(`/dashboard/business-owner/transactions/${uuid}/cancel`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to cancel transaction"
      );
    }
  }
);

export const getTransactionHistory = createAsyncThunk(
  "transaction/getTransactionHistory",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add query parameters if provided
      if (params.status) queryParams.append('status', params.status);
      if (params.date_range) queryParams.append('date_range', params.date_range);
      if (params.search) queryParams.append('search', params.search);
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      
      const queryString = queryParams.toString();
      const url = `/dashboard/business-owner/reports/transactions${queryString ? `?${queryString}` : ''}`;
      
      const response = await http.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch transaction history"
      );
    }
  }
);

export const getTransactionSummary = createAsyncThunk(
  "transaction/getTransactionSummary",
  async (uuid, { rejectWithValue }) => {
    try {
      const response = await http.get(`/dashboard/business-owner/transactions/${uuid}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to get transaction summary"
      );
    }
  }
);

export const getCustomerByMobile = createAsyncThunk(
  "transaction/getCustomerByMobile",
  async (mobile, { rejectWithValue }) => {
    try {
      const response = await http.post("/dashboard/business-owner/customers/get-by-mobile", {
        mobile: mobile
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Customer not found"
      );
    }
  }
);

const transactionSlice = createSlice({
  name: "transaction",
  initialState,
  reducers: {
    clearTransaction: (state) => {
      state.currentTransaction = null;
      state.transactionSummary = null;
      state.customer = null;
      state.error = null;
      state.status = "idle";
    },
    clearTransactionSummary: (state) => {
      state.transactionSummary = null;
    },
    clearTransactionHistory: (state) => {
      state.transactionHistory = [];
      state.historyMeta = null;
    },
    clearCustomer: (state) => {
      state.customer = null;
      state.customerError = null;
    },
    clearError: (state) => {
      state.error = null;
      state.customerError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create transaction (new business flow)
      .addCase(createTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "loading";
      })
      .addCase(createTransaction.fulfilled, (state, action) => {
        state.loading = false;
        console.log('createTransaction fulfilled - action.payload:', action.payload);
        // The API returns the transaction data in action.payload.data
        state.currentTransaction = action.payload.data || action.payload;
        state.transactionSummary = action.payload.data || action.payload;
        state.status = "success";
      })
      .addCase(createTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = "failed";
      })
      // Initiate transaction (legacy for admin)
      .addCase(initiateTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.status = "loading";
      })
      .addCase(initiateTransaction.fulfilled, (state, action) => {
        state.loading = false;
        console.log('initiateTransaction fulfilled - action.payload:', action.payload);
        // The API returns the transaction data in action.payload.data
        state.currentTransaction = action.payload.data || action.payload;
        state.status = "success";
      })
      .addCase(initiateTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = "failed";
      })
      // Fulfill transaction
      .addCase(fulfillTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fulfillTransaction.fulfilled, (state, action) => {
        state.loading = false;
        console.log('fulfillTransaction fulfilled - action.payload:', action.payload);
        state.currentTransaction = action.payload.data || action.payload;
        state.status = "success";
      })
      .addCase(fulfillTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = "failed";
      })
      // Get transaction
      .addCase(getTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTransaction.fulfilled, (state, action) => {
        state.loading = false;
        console.log('getTransaction fulfilled - action.payload:', action.payload);
        state.currentTransaction = action.payload.data || action.payload;
      })
      .addCase(getTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Cancel transaction
      .addCase(cancelTransaction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelTransaction.fulfilled, (state, action) => {
        state.loading = false;
        console.log('cancelTransaction fulfilled - action.payload:', action.payload);
        state.currentTransaction = action.payload.data || action.payload;
        state.status = "success";
      })
      .addCase(cancelTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.status = "failed";
      })
      // Get transaction history
      .addCase(getTransactionHistory.pending, (state) => {
        state.historyLoading = true;
        state.error = null;
      })
      .addCase(getTransactionHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        console.log('getTransactionHistory fulfilled - action.payload:', action.payload);
        // Handle the new API response structure
        if (action.payload.data && Array.isArray(action.payload.data)) {
          state.transactionHistory = action.payload.data;
          // Handle pagination object
          state.historyMeta = action.payload.pagination || action.payload.meta || null;
        } else if (Array.isArray(action.payload)) {
          state.transactionHistory = action.payload;
          state.historyMeta = null;
        } else {
          state.transactionHistory = action.payload.data || [];
          state.historyMeta = action.payload.pagination || action.payload.meta || null;
        }
      })
      .addCase(getTransactionHistory.rejected, (state, action) => {
        state.historyLoading = false;
        state.error = action.payload;
        state.transactionHistory = [];
        state.historyMeta = null;
      })
      // Get transaction summary
      .addCase(getTransactionSummary.pending, (state) => {
        state.summaryLoading = true;
        state.error = null;
      })
      .addCase(getTransactionSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        console.log('getTransactionSummary fulfilled - action.payload:', action.payload);
        const transactionData = action.payload.data || action.payload;
        state.transactionSummary = transactionData;
        
        // If this is a pending transaction, also set it as current transaction
        if (transactionData.status === 'INITIATED') {
          state.currentTransaction = transactionData;
          // Set customer data if available
          if (transactionData.customer) {
            state.customer = transactionData.customer;
          }
        }
      })
      .addCase(getTransactionSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.error = action.payload;
      })
      // Get customer by mobile
      .addCase(getCustomerByMobile.pending, (state) => {
        state.customerLoading = true;
        state.customerError = null;
      })
      .addCase(getCustomerByMobile.fulfilled, (state, action) => {
        state.customerLoading = false;
        console.log('getCustomerByMobile fulfilled - action.payload:', action.payload);
        // Handle different possible response structures
        state.customer = action.payload?.data || action.payload;
      })
      .addCase(getCustomerByMobile.rejected, (state, action) => {
        state.customerLoading = false;
        state.customerError = action.payload;
        state.customer = null;
      });
  },
});

export const { clearTransaction, clearTransactionSummary, clearTransactionHistory, clearCustomer, clearError } = transactionSlice.actions;
export default transactionSlice.reducer;